import { http, HttpResponse } from 'msw';
import type { RequestHandler } from 'msw';

import { completeOnboardingTask } from '../onboardingProgress';

import { getDemoMember } from './demoMember';
import { END_STEP, StepAnswers, nextStep, walkFrom } from './formationJourney';

import { TaskStatuses } from 'modules/Tasks/types';

/**
 * Mock of the onboarding questionnaire API (`/onboarding/v1/questionnaire/`) for the "Complete your
 * application" formation flow. The server owns the route, so this mirrors the backend's formation
 * journey (`hyke/onboarding/questionnaire/steps/formation.py`): the same steps, the same branches,
 * and the same rules for advancing, versioning and walking the route ahead. Resets on reload.
 *
 * The real flow ends by e-signing the SS-4 through HelloSign, which can't run in a demo. So the
 * demo's application is complete once Review is submitted: the application task comes back
 * COMPLETED, which the Sign step reads as already signed and redirects to the dashboard.
 */

const APPLICATION_TASK_NAME = 'complete_application';
const ENTRY_STEP = 'intro';

interface QuestionnaireState {
    version: number;
    currentStep: string;
    journeyRoute: string[];
    steps: Record<string, StepAnswers>;
    isApplicationCompleted: boolean;
}

function newState(): QuestionnaireState {
    return {
        version: 0,
        currentStep: ENTRY_STEP,
        journeyRoute: walkFrom(ENTRY_STEP, {}),
        steps: {},
        isApplicationCompleted: false,
    };
}

let state = newState();

// The spouse the married branch creates. The backend derives the member's `additionalshareholdername`
// from it (the spouse's full name), which is what the Review step reads to call the member married.
const UNMARRIED_SHAREHOLDER_NAME = '---';
type Spouse = Record<string, unknown> & { id: number };
let spouse: Spouse | null = null;

function setSpouse(next: Spouse | null): void {
    spouse = next;
    getDemoMember().additionalshareholdername = next ? next.fullname : UNMARRIED_SHAREHOLDER_NAME;
}

function withFullname(fields: Record<string, unknown>): Record<string, unknown> {
    const fullname = [fields.first_name, fields.middle_name, fields.last_name].filter(Boolean).join(' ');
    return { ...fields, fullname };
}

/** Start the application over, e.g. on (re)entering the flow from the demo controls or on exit. */
export function resetQuestionnaire(): void {
    state = newState();
    if (spouse) {
        setSpouse(null);
    }
}

/** The backend keeps whether an SSN was given, never the SSN itself. */
function answersFor(step: string, data: StepAnswers, member: Record<string, unknown>): StepAnswers {
    if (step !== 'personal_info') {
        return data;
    }
    const { SSN: ssn, ...rest } = data;
    return { ssn_provided: Boolean(ssn ?? member.SSN), ...rest };
}

function buildResponse() {
    return {
        questionnaire: {
            orchestrator: 'base',
            task_id: `demo-task-${APPLICATION_TASK_NAME}`,
            flow: 'formation',
            version: state.version,
            current_step: state.currentStep,
            journey_route: state.journeyRoute,
            steps: state.steps,
        },
        task: state.isApplicationCompleted
            ? {
                  id: `demo-task-${APPLICATION_TASK_NAME}`,
                  status: TaskStatuses.COMPLETED,
                  context: {},
                  task_template: { name: APPLICATION_TASK_NAME, display_name: APPLICATION_TASK_NAME },
              }
            : null,
        member: getDemoMember(),
    };
}

interface SubmitBody {
    version?: number;
    advance?: boolean;
    data?: StepAnswers;
    member?: Record<string, unknown>;
}

export default function buildQuestionnaireHandlers(): RequestHandler[] {
    return [
        http.get('*/onboarding/v1/questionnaire/', () => HttpResponse.json(buildResponse())),

        http.post('*/onboarding/v1/questionnaire/:step/submit/', async ({ params, request }) => {
            const step = String(params.step);
            const {
                version,
                advance = true,
                data = {},
                member,
            } = (await request.json().catch(() => ({}))) as SubmitBody;

            const stepIndex = state.journeyRoute.indexOf(step);
            if (stepIndex === -1 || stepIndex > state.journeyRoute.indexOf(state.currentStep)) {
                return HttpResponse.json({ error: 'step_not_reached' }, { status: 400 });
            }
            if (version !== state.version) {
                return HttpResponse.json({ error: 'stale_state' }, { status: 409 });
            }

            const demoMember = getDemoMember();
            if (member) {
                Object.assign(demoMember, member);
            }
            state.steps = { ...state.steps, [step]: answersFor(step, data, demoMember) };

            const next = advance ? nextStep(step, state.steps) : null;
            if (next !== null) {
                state.journeyRoute = [...state.journeyRoute.slice(0, stepIndex + 1), ...walkFrom(next, state.steps)];
                state.currentStep = next;
            }
            if (state.currentStep === END_STEP && !state.isApplicationCompleted) {
                state.isApplicationCompleted = true;
                completeOnboardingTask(APPLICATION_TASK_NAME);
            }
            state.version += 1;

            return HttpResponse.json(buildResponse());
        }),

        http.get('*/spouses/', () => HttpResponse.json(spouse ? [spouse] : [])),
        http.post('*/spouses/', async ({ request }) => {
            const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
            setSpouse({ ...withFullname(body), id: 1 });
            return HttpResponse.json(spouse, { status: 201 });
        }),
        http.put('*/spouses/:id/', async ({ request }) => {
            const body = (await request.json().catch(() => ({}))) as Record<string, unknown>;
            setSpouse({ ...withFullname({ ...spouse, ...body }), id: 1 });
            return HttpResponse.json(spouse);
        }),
        http.delete('*/spouses/:id/', () => {
            setSpouse(null);
            return new HttpResponse(null, { status: 204 });
        }),

        // Business-name availability through the vendor check (the gated path); the OpenCorporates path
        // is mocked in overrides.ts. Always available so the prospect can continue.
        http.post('*/formation/business-name-check/', async ({ request }) => {
            const body = (await request.json().catch(() => ({}))) as { business_name?: string; state?: string };
            return HttpResponse.json({
                business_name: body.business_name ?? '',
                state: body.state ?? '',
                status: 'AVAILABLE',
                message: '',
                rejection_reason: '',
                reasons: [],
            });
        }),
    ];
}
